import React from "react";
import Overlay from "../Overlay";
import SimpleTable from "../tables/SimpleTable";
import { getBoolSelectVal, hideModal, showModalNoOutsideClick } from "../../util/FormatUtil";
import { sweetalert } from "../../App";
import { Column } from "../tables/TableBase";
import { Sorter, TableOrder } from "../../sorting/Sorter";
import AdminAPI from "../../network/AdminAPI";
import FAQAPI from "../../network/FAQAPI";
import { Category } from "../../types/Category";
import { CategoryManagementModal } from "../modals/CategoryManagementModal";

interface CategoryManagementState {
  showLoading: boolean;
  selectedCategory: Category;
  tableData: Category[];
  direction?: TableOrder;
  isGlobal: boolean;
}

export class CategoryManagement extends React.Component<any, CategoryManagementState> {

  constructor(props) {
    super(props);
    this.state = {
      showLoading: false,
      selectedCategory: {} as Category,
      tableData: [],
      direction: "asc",
      isGlobal: false,
    };
    this.createOrModifyCategory = this.createOrModifyCategory.bind(this);
  }

  componentDidMount() {
    document.title = 'Category Management Page';
    AdminAPI.getPermissions().then((data) => {
      this.setState({ isGlobal: data.isGlobal });
    });
    this.loadCategories();
  }

  loadCategories() {
    this.setState({ showLoading: true }, async () => {
      try {
        let response = await FAQAPI.getAllCategories();
        if (!response.success) {
          this.setState({ showLoading: false, tableData: [] });
          return sweetalert.fire({ icon: "error", title: "", text: "Unable to load categories at this time." });
        }
        this.setState({ tableData: response.data || [], showLoading: false });
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
        return sweetalert.fire({ icon: "error", title: "", text: "Unable to load categories at this time." });
      }
    });
  }

  createOrModifyCategory(category: Category) {
    this.setState({ showLoading: true }, async () => {
      try {
        let response = category.ID === null
          ? await FAQAPI.createCategory(category)
          : await FAQAPI.editCategory(category);

        if (!response.success) {
          let msg = response.reason ? response.reason : "Unable to save category at this time.";
          this.setState({ showLoading: false });
          return sweetalert.fire({ icon: "error", title: "", text: msg });
        }

        sweetalert.fire({ icon: "success", title: "", text: "Category saved" }).then(() => {
          this.setState({ selectedCategory: {} as Category }, () => this.loadCategories());
        });
      } catch (e) {
        console.error(e);
        this.setState({ showLoading: false });
        return sweetalert.fire({ icon: "error", title: "", text: "Unable to save category at this time." });
      }
    });
  }

  useSorter(col: Column) {
    let sorter = new Sorter<any>();
    this.setState({
      tableData: sorter.sortByKey(this.state.tableData, col.key as keyof any, this.state.direction),
      direction: this.state.direction === "asc" ? "desc" : "asc",
    });
  }

  render() {
    return (
      <React.Fragment>
        <Overlay show_loading={this.state.showLoading} />
        <CategoryManagementModal
          selectedCategory={this.state.selectedCategory}
          onSubmit={(category) => {
            hideModal(CategoryManagementModal.ID);
            this.createOrModifyCategory(category);
          }}
        />

        <div className="container-fluid">
          <div className={"row"}>
            <div className="col-12 col-md-12 col-lg-8 col-xl-5 pt-2">
              <main id="main-content" tabIndex={-1} aria-label="Category Management">
                <div className="card mb-2">
                  <div className="card-header verlag-bold">
                    <h3>Category Management</h3>
                  </div>
                  <div className="card-body">
                    <p className="m-0 text-muted">
                      Categories group FAQs on the Resources page. Disabling a category hides its
                      FAQs from users.
                    </p>
                  </div>
                  <div className="card-footer">
                    {this.state.isGlobal &&
                      <button
                        className="btn btn-outline-success float-right"
                        onClick={() =>
                          this.setState(
                            { selectedCategory: { ID: null, Name: "", IsActive: 1 } },
                            () => showModalNoOutsideClick(CategoryManagementModal.ID)
                          )
                        }
                      >
                        Create New
                      </button>
                    }
                  </div>
                </div>
              </main>
            </div>
            {this.state.tableData && this.state.tableData.length > 0 && (
              <div className="col-12 col-md-12 col-lg-12 pt-2">
                <div className="card mt-2" style={{ marginBottom: '4rem' }}>
                  <div className="card-header verlag-bold stickToTop">
                    <h4 className="text-center text-md-left">
                      Categories
                      <section className="tableHeaderSection float-md-right d-flex justify-content-around">
                        <h4 className={"float-md-right"} aria-label="Total Records" role="alert">
                          Total: {this.state.tableData.length}
                        </h4>
                      </section>
                    </h4>
                  </div>
                  <div className="card-body p-0 m-0 table-responsive">
                    <SimpleTable
                      columns={[
                        {
                          label: "Category",
                          key: "Name",
                          popoverText: "Click to Sort By Category",
                          rawFormat: (val) => {
                            if (!this.state.isGlobal) {
                              return <p>{val.Name}</p>;
                            }
                            return (
                              <a
                                href={"#"}
                                className={'tableNameLinkColor'}
                                onClick={() => {
                                  this.setState({ selectedCategory: val }, () =>
                                    showModalNoOutsideClick(CategoryManagementModal.ID)
                                  );
                                }}
                              >
                                {val.Name}
                              </a>
                            );
                          },
                        },
                        {
                          label: "Is Active?",
                          key: "IsActive",
                          popoverText: "Click to Sort By Is Active",
                          rawFormat: (val) => {
                            let string = getBoolSelectVal(val.IsActive);
                            return <p>{string.label}</p>;
                          },
                        },
                      ]}
                      table_data={this.state.tableData}
                      columnClickedCallback={(col) => {
                        this.useSorter(col);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  }
}
